import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import api, { fileToBase64 } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const emptyEvent = { title: "", date: "", description: "", photo: "", price: "", capacity: "", published: true };

export default function EventsManager() {
  const [items, setItems] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showTickets, setShowTickets] = useState(null);

  const load = () => {
    api.get("/admin/events").then((r) => setItems(r.data)).catch(() => {});
    api.get("/admin/tickets").then((r) => setTickets(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const soldFor = (eventId) =>
    tickets
      .filter((t) => t.event_id === eventId && t.status === "confirmee")
      .reduce((s, t) => s + t.quantity, 0);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...editing,
        price: parseFloat(String(editing.price).replace(",", ".")) || 0,
        capacity: parseInt(editing.capacity, 10) || 0,
      };
      if (editing.id) {
        await api.put(`/admin/events/${editing.id}`, payload);
      } else {
        await api.post("/admin/events", payload);
      }
      toast.success("Événement enregistré.");
      setEditing(null);
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/events/${id}`);
    toast.success("Événement supprimé.");
    load();
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const base64 = await fileToBase64(file);
    setEditing((s) => ({ ...s, photo: base64 }));
  };

  const set = (key) => (e) => setEditing((s) => ({ ...s, [key]: e.target.value }));

  return (
    <div data-testid="events-manager">
      <div className="mb-8 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-ivory">Événements — {items.length}</h2>
        <button onClick={() => setEditing({ ...emptyEvent })} className="btn-primary !h-10 !px-5 !text-[11px]" data-testid="event-add-button">
          <Plus className="h-4 w-4" strokeWidth={1.5} />
          Ajouter
        </button>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="event-edit-modal">
          <form onSubmit={save} className="w-full max-w-xl space-y-4 rounded-lg border border-white/10 bg-coal p-8">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-ivory">
                {editing.id ? "Modifier l'événement" : "Nouvel événement"}
              </h3>
              <button type="button" onClick={() => setEditing(null)} aria-label="Fermer">
                <X className="h-5 w-5 text-ivory/50 hover:text-ivory" strokeWidth={1.5} />
              </button>
            </div>
            <input required value={editing.title} onChange={set("title")} placeholder="Titre" className="input-dark !h-11" data-testid="event-title-input" />
            <input type="date" value={editing.date} onChange={set("date")} className="input-dark !h-11 [color-scheme:dark]" data-testid="event-date-input" />
            <textarea rows={3} value={editing.description} onChange={set("description")} placeholder="Description" className="input-dark !h-auto py-3" data-testid="event-description-input" />
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
                  Prix par place (€, 0 = pas de vente en ligne)
                </label>
                <input value={editing.price} onChange={set("price")} placeholder="0" className="input-dark !h-11" data-testid="event-price-input" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
                  Jauge (0 = illimitée)
                </label>
                <input type="number" min="0" value={editing.capacity} onChange={set("capacity")} placeholder="0" className="input-dark !h-11" data-testid="event-capacity-input" />
              </div>
            </div>
            <div>
              {editing.photo && (
                <img src={editing.photo} alt="" className="mb-3 h-24 w-40 rounded-md border border-white/10 object-cover" />
              )}
              <input type="file" accept="image/*" onChange={uploadPhoto} className="text-xs text-ivory/60 file:mr-3 file:rounded-md file:border-0 file:bg-white/10 file:px-3 file:py-2 file:text-xs file:text-ivory" data-testid="event-photo-upload" />
            </div>
            <label className="flex items-center gap-3 text-sm text-ivory/70">
              Publié
              <Switch checked={editing.published} onCheckedChange={(v) => setEditing((s) => ({ ...s, published: v }))} data-testid="event-published-switch" />
            </label>
            <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="event-save-button">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </form>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-ivory/40" data-testid="events-empty">Aucun événement pour le moment.</p>
      ) : (
        <div className="space-y-2" data-testid="events-list-admin">
          {items.map((event) => (
            <div key={event.id} className="rounded-lg border border-white/10 bg-coal/60 px-5 py-3.5" data-testid={`event-row-${event.id}`}>
              <div className="flex items-center gap-4">
                <span className="w-28 shrink-0 text-xs uppercase tracking-wider text-gold/70">{event.date || "—"}</span>
                <span className={`flex-1 truncate text-sm ${event.published ? "text-ivory" : "text-ivory/35 line-through"}`}>
                  {event.title}
                </span>
                {event.price > 0 && (
                  <button
                    onClick={() => setShowTickets(showTickets === event.id ? null : event.id)}
                    className="shrink-0 rounded-md border border-gold/30 px-2.5 py-1 text-[11px] font-semibold text-gold transition-colors hover:bg-gold/10"
                    data-testid={`event-sales-${event.id}`}
                  >
                    {soldFor(event.id)} place(s) vendue(s)
                  </button>
                )}
                <button onClick={() => setEditing({ ...event })} aria-label="Modifier" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/50 transition-colors hover:border-gold hover:text-gold" data-testid={`event-edit-${event.id}`}>
                  <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
                <button onClick={() => remove(event.id)} aria-label="Supprimer" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/40 transition-colors hover:border-red-400/50 hover:text-red-400" data-testid={`event-delete-${event.id}`}>
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
              </div>
              {showTickets === event.id && (
                <ul className="mt-3 space-y-1.5 border-t border-white/5 pt-3" data-testid={`event-tickets-${event.id}`}>
                  {tickets.filter((t) => t.event_id === event.id).length === 0 ? (
                    <li className="text-xs text-ivory/40">Aucune vente pour le moment.</li>
                  ) : (
                    tickets
                      .filter((t) => t.event_id === event.id)
                      .map((t) => (
                        <li key={t.id} className="flex justify-between gap-4 text-xs text-ivory/55">
                          <span>
                            {t.quantity} × {t.name} ({t.email})
                          </span>
                          <span className={t.status === "confirmee" ? "text-gold" : "text-ivory/35"}>
                            {t.status === "confirmee" ? "Payé" : "En attente"}
                          </span>
                        </li>
                      ))
                  )}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
